import { useNavigate } from "react-router-dom"
import {
  FormCard,
  FormCardBlock,
  FormCardRow,
} from "@/components/ux/form.card"
import { useForm } from "react-hook-form"
import { accountSchema } from "./account.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { User, useUserActions } from "@/api/users"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useUserUpdateRequest } from "@dynamic-labs/sdk-react-core"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"

interface AccountFormProps {
  user: User;
}

export default function AccountForm({ user }: AccountFormProps) {
  const navigate = useNavigate()
  const { update } = useUserActions()
  const { updateUser } = useUserUpdateRequest()
  const form = useForm<z.infer<typeof accountSchema>>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
  })

  const onSubmit = async (data: z.infer<typeof accountSchema>) => {
    await update(data)
    await updateUser(data)
    toast.success("Account details updated")
    navigate("/settings")
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormCard
          title="Account Details"
          actions={
            <Button type="submit" variant="outline">
              Save changes
            </Button>
          }
        >
          <FormCardBlock>
            <FormField
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormCardRow>
                    <FormLabel required className="w-6/12 max-w-64">
                      First name
                    </FormLabel>
                    <div className="flex-grow md:max-w-64 w-full">
                      <FormControl>
                        <Input placeholder="Your first name" {...field} />
                      </FormControl>
                      <FormMessage className="pt-2" />
                    </div>
                  </FormCardRow>
                </FormItem>
              )}
            />
            <FormField
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormCardRow>
                    <FormLabel required className="w-6/12 max-w-64">
                      Last name
                    </FormLabel>
                    <div className="flex-grow md:max-w-64 w-full">
                      <FormControl>
                        <Input placeholder="Your last name" {...field} />
                      </FormControl>
                      <FormMessage className="pt-2" />
                    </div>
                  </FormCardRow>
                </FormItem>
              )}
            />
          </FormCardBlock>
          <FormCardBlock>
            <FormCardRow>
              <FormLabel className="w-6/12 max-w-64">Email address</FormLabel>
              <div className="text text-sm font-normal">{user.email}</div>
            </FormCardRow>
          </FormCardBlock>
        </FormCard>
      </form>
    </Form>
  )
}
